import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Router } from '@angular/router'
import { injectQuery, injectMutation, injectQueryClient } from '@ngneat/query'
import { Organization } from './organizations.service'
import { queryKeys } from './query-keys'
import { ToastService } from './toast.service'
import { Template, TemplateItem } from './template-editor.component/types/template.types'

export type TemplateWithoutId = Omit<Template, 'id'>

export type TemplateWithOrganization = Template & {
  organization: Organization
}

@Injectable({
  providedIn: 'root',
})
export class TemplatesService {
  httpClient = inject(HttpClient)
  query = injectQuery()
  mutation = injectMutation()
  queryClient = injectQueryClient()
  toastService = inject(ToastService)
  router = inject(Router)

  getTemplateById(id: number) {
    return this.query({
      queryKey: queryKeys.templates.byId(id),
      queryFn: () => this.httpClient.get<TemplateWithOrganization>(`/api/templates/${id}`),
    }).result$
  }

  getTemplateModels() {
    return this.query({
      queryKey: queryKeys.templates.models.all,
      queryFn: () => this.httpClient.get<TemplateWithOrganization[]>('/api/templates/models/admin'),
    }).result$
  }

  getTemplateModelsForOrganization(organizationId: number) {
    return this.query({
      queryKey: queryKeys.templates.models.byOrganization(organizationId),
      queryFn: () => this.httpClient.get<TemplateWithOrganization[]>(`/api/templates/models?organizationId=${organizationId}`),
    }).result$
  }

  getTemplateItems(callId: number) {
    return this.query<TemplateItem[]>({
      queryKey: queryKeys.templates.byCallId(callId),
      queryFn: () => this.httpClient.get<TemplateItem[]>(`/api/calls/${callId}/template-items`),
      refetchInterval: 10000,
    }).result$
  }

  createTemplate() {
    return this.mutation({
      mutationFn: (template: TemplateWithoutId) => this.httpClient.post<Template>('/api/templates', template),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.base })
        this.toastService.show('TEMPLATES.CREATED')
      },
      onError: (error) => {
        this.toastService.show('TEMPLATES.CREATE_FAILED')
      },
    })
  }

  updateTemplate() {
    return this.mutation({
      mutationFn: (template: Template) => this.httpClient.put<Template>(`/api/templates/${template.id}`, template),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.base })
        this.toastService.show('TEMPLATES.UPDATED')
      },
      onError: (error) => {
        this.toastService.show('TEMPLATES.UPDATE_FAILED')
      },
    })
  }

  updateTemplateItems() {
    return this.mutation({
      mutationFn: ({ callId, items }: { callId: number; items: TemplateItem[] }) =>
        this.httpClient.put<TemplateItem[]>(`/api/calls/${callId}/template-items`, { items }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.byCallId(params.callId) })
        this.toastService.show('TEMPLATES.TEMPLATE_SAVED')
      },
      onError: () => {
        this.toastService.show('TEMPLATES.SAVE_FAILED')
      },
    })
  }

  updateTemplateItemsSuper() {
    return this.mutation({
      mutationFn: ({ callId, items }: { callId: number; items: TemplateItem[] }) =>
        this.httpClient.put<TemplateItem[]>(`/api/calls/${callId}/template-items-super`, { items }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(params.callId) })
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.byCallId(params.callId) })
        this.toastService.show('TEMPLATES.TEMPLATE_SAVED')
      },
      onError: () => {
        this.toastService.show('TEMPLATES.SAVE_FAILED')
      },
    })
  }

  deleteTemplate() {
    return this.mutation({
      mutationFn: (templateId: number) => this.httpClient.delete(`/api/templates/${templateId}`),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.base })
        this.toastService.show('TEMPLATES.DELETED')
      },
      onError: (error) => {
        this.toastService.show('TEMPLATES.DELETE_FAILED')
      },
    })
  }

  saveCallTemplateAsModel() {
    return this.mutation({
      mutationFn: ({ name, items }: { organizationId: number; name: string; items: TemplateItem[] }) =>
        this.httpClient.post<Template>('/api/templates/save-as', {
          name,
          items,
        }),
      onSuccess: (_, params) => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.models.byOrganization(params.organizationId) })
        this.toastService.show('TEMPLATES.SAVE_AS_SUCCESS')
      },
      onError: (error) => {
        this.toastService.show('TEMPLATES.SAVE_AS_FAILED')
      },
    })
  }

  exportTemplate() {
    return this.mutation({
      mutationFn: ({ templateId }: { templateId: number; templateName: string }) =>
        this.httpClient.get<{ name: string; organizationId: number; items: TemplateItem[] }>(`/api/templates/${templateId}/export`),
      onSuccess: (data, params) => {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${params.templateName}-template.json`
        link.click()
        window.URL.revokeObjectURL(url)
        this.toastService.show('TEMPLATES.EXPORT_SUCCESS')
      },
      onError: () => {
        this.toastService.show('TEMPLATES.EXPORT_FAILED')
      },
    })
  }

  importTemplate() {
    return this.mutation({
      mutationFn: (data: { name: string; organizationId: number; items: TemplateItem[] }) => this.httpClient.post<Template>('/api/templates/import', data),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.base })
        this.toastService.show('TEMPLATES.IMPORT_SUCCESS')
      },
      onError: (error) => {
        this.toastService.show('TEMPLATES.IMPORT_FAILED')
      },
    })
  }
}
